/**
 * Contract-type "playbook" — the editorial reference for what each contract type
 * is and how its lifecycle works, sourced from the Contract & Billing Playbook.
 *
 * WHY this shape: the content is *type-level* reference (every "New Contract"
 * shares the same lifecycle), not per-record data — so it lives as typed static
 * content keyed by a normalized archetype key. The view renders
 * `resolvePlaybook(name, override)` = STATIC archetype ⊕ optional backend data,
 * so when an admin edits a type (Phase 2, persisted Ops-side) or a real data
 * source arrives, the same shape fills in with ZERO view changes. Sections render
 * only when present; nothing here is load-bearing for an unmatched type.
 */

export type BadgeTone = "blue" | "green" | "purple" | "coral" | "yellow";
export type CalloutTone = "rule" | "warn" | "note";

export interface LifecycleStage {
  /** Tiny uppercase label, e.g. "Create". */
  stage: string;
  title: string;
  detail?: string;
}

export interface MustHaveField {
  label: string;
  /** Hard requirement at creation (vs. recommended). */
  required?: boolean;
}

export interface ThreeOutputs {
  billingStart?: string;
  churn?: string;
  bookedVsBilled?: string;
  renewals?: string;
}

export interface Callout {
  tone: CalloutTone;
  heading?: string;
  body: string;
}

export interface KeyPoints {
  heading: string;
  items: string[];
}

export interface ContractTypePlaybook {
  /** Normalized archetype key (stable; not the editable display name). */
  key: string;
  label: string;
  badge?: { text: string; tone: BadgeTone };
  /** One-liner shown in the quick view. */
  summary: string;
  lifecycle?: LifecycleStage[];
  whenToUse?: string;
  mustHave?: MustHaveField[];
  billingStart?: string;
  termRenewal?: string;
  howItEnds?: string[];
  /** Type-specific bullet section (e.g. PO Upsell points, License expansion). */
  keyPoints?: KeyPoints;
  outputs?: ThreeOutputs;
  /** Type-specific rule/warn/note callouts. */
  callouts?: Callout[];
}

export const PLAYBOOK: Record<string, ContractTypePlaybook> = {
  new: {
    key: "new",
    label: "New Contract",
    badge: { text: "Direct", tone: "blue" },
    summary: "A net-new direct logo — a signed order for an account with no active contract.",
    lifecycle: [
      { stage: "Create", title: "Signed", detail: "direct logo, no existing contract" },
      { stage: "Start", title: "Billing start", detail: "first full month base line bills" },
      { stage: "Live", title: "Evergreen renewal", detail: "+ term, rolling" },
      { stage: "End", title: "Churn / Replace / Debook" },
    ],
    whenToUse:
      "A net-new direct logo — not an addition to an existing customer, not a re-paper, not billed under a partner. Trigger is an executed order for an account with no active contract.",
    mustHave: [
      { label: "ACCOUNT_ID (→ QBID)", required: true },
      { label: "base/subscription line", required: true },
      { label: "MRR_TOTAL", required: true },
      { label: "TERM_MONTHS (12)" },
      { label: "IS_AUTO_RENEW" },
      { label: "RENEWAL_TERM" },
      { label: "NOTICE_PERIOD_DAYS" },
      { label: "billing effective date" },
    ],
    billingStart:
      "Billing start = the first full month the base/subscription line bills at full rate. The prorated partial first month (daily proration to land on the 1st) does not start the clock.",
    termRenewal:
      "Term runs TERM_MONTHS from billing start; evergreen by default — rolls into the same term with no new row. NEXT_RENEWAL_AT = billing start + (N × term). Exceptions: IS_AUTO_RENEW=false (expires) and RENEWAL_TERM=MONTH_TO_MONTH (apply MTM_UPLIFT_PCT).",
    howItEnds: [
      "Churn — CS logs CONTRACT_CANCELED_AT (notice) + STOP_BILLING_AT (end); 1 month at $0 confirms.",
      "Replace — restructure/price/qty change via a Replacement (REPLACED_CONTRACT_ID).",
      "Debook — never billed and abandoned → manual flag, removed from booked-unbilled.",
    ],
    outputs: {
      billingStart: "Billed MRR once the first full month bills.",
      churn: "Active until $0.",
      bookedVsBilled: "Full MRR in the gap sign→first-full-month, partial during ramp.",
      renewals: "Appears at NEXT_RENEWAL_AT minus the notice window.",
    },
    callouts: [
      {
        tone: "note",
        body: "ACCOUNT_ID originates from the HubSpot closed-deal import — the most common break is a missing mapping, which drops the contract out of reconciliation.",
      },
    ],
  },

  upsell: {
    key: "upsell",
    label: "Upsell / Add-on Items",
    badge: { text: "Add-on", tone: "green" },
    summary: "Expansion of an existing active customer — a new line item added to a contract already in force.",
    lifecycle: [
      { stage: "Create", title: "Added to parent", detail: "new line on existing contract" },
      { stage: "Start", title: "Own billing start", detail: "for the added line" },
      { stage: "Live", title: "Co-terms w/ parent", detail: "shares parent renewal" },
      { stage: "End", title: "With parent / Contraction" },
    ],
    whenToUse:
      "Expansion of an existing active customer — a new line item added to a contract already in force. Rule of thumb: add a new line → Upsell; remove an entire line → Contraction (amendment); change price/qty of an existing line → Replacement.",
    mustHave: [
      { label: "AMENDED_CONTRACT_ID (parent)", required: true },
      { label: "added subscription lines", required: true },
      { label: "MRR_TOTAL (incremental)", required: true },
      { label: "billing effective date", required: true },
    ],
    billingStart:
      "The added lines carry their own first-full-month start (can be later than the parent's); automated billing picks it up from the listed effective date.",
    termRenewal:
      "Co-terminates with the parent — adopts the parent's renewal date; mid-term upsells are prorated to the parent's remaining term. Upsells cannot be independent.",
    howItEnds: [
      "Contraction — the add-on line is removed via an amendment, billing-end date = when that line's billing ends (removes the whole line; a qty/price change is a Replacement).",
      "Parent churns → all amended/upsell contracts churn with it (cascade).",
      "Debook — rare but possible.",
    ],
    outputs: {
      billingStart: "Incremental billed MRR once its first full month bills (line-level).",
      churn: "Line-level — a dropped add-on reduces MRR without flagging the whole account.",
      bookedVsBilled: "Incremental MRR in the gap until billed.",
      renewals: "Folds into the parent's single renewal line.",
    },
    callouts: [
      {
        tone: "warn",
        heading: "Visibility",
        body: "Upsell billing rolls up under the parent account's counterparty — not a separate line — so confirming it actually started needs line-item-level matching.",
      },
    ],
  },

  poupsell: {
    key: "poupsell",
    label: "PO Upsell",
    badge: { text: "Reseller add-on", tone: "green" },
    summary: "The Upsell lifecycle with a Purchase Order as the parent instead of a direct contract.",
    whenToUse:
      "The Upsell lifecycle with a Purchase Order as the parent instead of a direct contract. Everything on the Upsell type applies, with the PO-specific points below.",
    mustHave: [
      { label: "parent PO link", required: true },
      { label: "company_id (ASSIGNED_COMPANY_ID)", required: true },
      { label: "reseller context", required: true },
      { label: "billing effective date", required: true },
    ],
    keyPoints: {
      heading: "PO-specific points",
      items: [
        "Co-terms with the PO; carries its own billing-start for the added line.",
        "Billed under whatever the PO is billed under — if bundled on the partner, it shows as an increase on the partner's number, not a separate line (line-item / manual detection).",
        "Cascades on PO replacement/churn; removing the added line is a contraction (amendment).",
        "Carries company_id for the same company-cleanliness reason as POs.",
      ],
    },
  },

  replacement: {
    key: "replacement",
    label: "Replacement",
    badge: { text: "Net", tone: "purple" },
    summary:
      "An existing contract changing in a way that isn't a pure addition — a price/qty change, restructure, or consolidation. Covers Replacement Contract / PO / Reseller / License.",
    lifecycle: [
      { stage: "Create", title: "Supersedes prior", detail: "via REPLACED_CONTRACT_ID" },
      { stage: "Start", title: "Billing changeover", detail: "old amounts → new amounts" },
      { stage: "Term", title: "New term", detail: "always, from effective date" },
      { stage: "Live", title: "Evergreen / next replace" },
    ],
    whenToUse:
      "When an existing contract changes in a way that isn't a pure addition: a price or quantity change to an already-included line, a restructure, or a consolidation. Also the vehicle for a renewal that comes with changes (a clean same-term renewal just rolls evergreen, no record). Variants: Replacement Contract / PO / Reseller / License.",
    mustHave: [
      { label: "REPLACED_CONTRACT_ID", required: true },
      { label: "new line items + MRR_TOTAL", required: true },
      { label: "billing effective date (term anchor)", required: true },
    ],
    billingStart:
      "Two senses of \"start.\" The account's billing tenure traces back through the chain to the original first full month (continuous billing — no fake churn-and-restart). The contract's term anchor is the replacement's billing effective date — when billing changes from old to new amounts. With replacements it's always the billing effective date for the term, never the sign date.",
    termRenewal:
      "A Replacement always carries a new term, even when it matches the previous one, calculated from the replacement's billing effective date. Evergreen applies afterward.",
    howItEnds: [
      "The replaced contract and all its associated upsells/amendments are closed (BILLING_EFFECTIVE_THROUGH / STOP_BILLING_AT set to the handoff).",
      "\"Replaced\" is inferred, not stored — a contract is replaced if its CONTRACT_ID appears as another's REPLACED_CONTRACT_ID; reconciliation uses this to keep it out of churn.",
      "Net bookings = new MRR − (replaced parent MRR + all associated amendment/upsell MRR).",
      "Replacement Reseller / License are legal-relationship re-papers, not amount/term changes.",
    ],
    outputs: {
      billingStart: "Lineage preserved → reads as continuously billed.",
      churn: "The replaced contract reads replaced, not churned — what stops the replacement double-count from masquerading as churn.",
      bookedVsBilled: "The net change flows.",
      renewals: "Follows the replacement's term anchor.",
    },
    callouts: [
      {
        tone: "note",
        heading: "Documentation patterns",
        body: "(1) A Replacement can write a non-standard initial term that renews into standard — e.g. a 6-month that renews into annual. (2) The catch-up Replacement — e.g. a 15-month term to pick up the rest of one term and re-anchor the renewal.",
      },
      {
        tone: "warn",
        body: "The big enforcement gap is on Replacement POs, which often leave REPLACED_CONTRACT_ID blank and can't be auto-netted.",
      },
    ],
  },

  reseller: {
    key: "reseller",
    label: "Reseller Agreement",
    badge: { text: "Partner", tone: "coral" },
    summary: "A partner who resells Cinch to their own customers — the legal parent of the child Purchase Orders.",
    lifecycle: [
      { stage: "Create", title: "Sign partner", detail: "legal parent of POs" },
      { stage: "Start", title: "Base fee (if any)", detail: "else $0, POs carry $" },
      { stage: "Live", title: "Evergreen", detail: "POs run independently" },
      { stage: "End", title: "Churn / Replace", detail: "POs don't cascade" },
    ],
    whenToUse:
      "Signing a partner who resells Cinch to their own customers. It's the legal parent; revenue rides on the child Purchase Orders (and PO Upsells). It becomes the RESELLER_CONTRACT_ID every child PO points back to.",
    billingStart:
      "If the agreement includes items/amounts, that becomes a base amount for the reseller's own account, billed exactly like a New/Replacement contract. Otherwise it's a $0 legal parent and all revenue is on the POs.",
    termRenewal:
      "Runs its term, renews evergreen as a legal relationship. Resellers get the same churn indicators as regular deals (cancel/notice date, end date, reason).",
    keyPoints: {
      heading: "Allocation & child POs",
      items: [
        "A reseller can carry a base allocation, with each company getting a higher per-company allocation (contrast the License pooled model).",
        "An account can hold both a license and a reseller agreement.",
        "Child POs are independent — each runs its own term and terminates on its own; a reseller agreement ending does not cascade to the POs.",
      ],
    },
    outputs: {
      billingStart: "Only if there's a base fee; otherwise invisible to billing.",
      churn: "Agreement-level, tracked like a regular deal.",
      bookedVsBilled: "The gap lives at the PO level.",
      renewals: "The agreement is its own renewal line; POs are separate.",
    },
    callouts: [
      {
        tone: "warn",
        heading: "Build gap",
        body: "A Replacement Reseller should re-attach the POs to the new agreement — there's no mechanism for that today (POs still point at the old RESELLER_CONTRACT_ID).",
      },
    ],
  },

  po: {
    key: "po",
    label: "Purchase Order",
    badge: { text: "Reseller child", tone: "coral" },
    summary: "A specific company billed under a reseller — the main dollar carrier in the partner structure.",
    lifecycle: [
      { stage: "Create", title: "Signed under reseller", detail: "a company billed via partner" },
      { stage: "Start", title: "Per-PO billing start", detail: "each company independently" },
      { stage: "Live", title: "Own annual renewal", detail: "independent of reseller" },
      { stage: "End", title: "Manual cancel / Replace" },
    ],
    whenToUse:
      "A specific company billed under a reseller — the main dollar carrier in the partner structure. Related: PO Upsell (a line added to a PO) and Replacement PO (price/qty change).",
    mustHave: [
      { label: "RESELLER_CONTRACT_ID (up-link)", required: true },
      { label: "company_id (ASSIGNED_COMPANY_ID)", required: true },
      { label: "TERM_MONTHS (12)" },
      { label: "billing effective date" },
      { label: "line items + MRR" },
    ],
    billingStart:
      "The ideal is every PO billed to the partner's (reseller's) QBID (bundled). In reality many POs get their own QBID/account. The sub-account roll-up (an app function to build) ties a PO's own account up under its reseller as a sub-account so partner-level and per-PO views both reconcile. Where billing bundles under the reseller, per-PO start needs line-item-level detection.",
    termRenewal:
      "Own 12-month term from its billing effective date, evergreen, with its own renewal date and notice window — independent of the reseller and sibling POs. A partner can have many POs renewing on different anniversaries.",
    howItEnds: [
      "PO churn is mostly a manual CS/Partner flag. When bundled under the partner, a dropped PO won't show as $0 — it just looks like a reduction/contraction on the partner's total, so the \"$0 = churn\" rule doesn't apply.",
      "Replacement PO needs REPLACED_CONTRACT_ID + RESELLER_CONTRACT_ID + company_id.",
    ],
    outputs: {
      billingStart:
        "Per-PO, attributed to the assigned company if it has its own QBID, else line-item detection under the reseller.",
      churn: "At PO/company grain, mostly manual.",
      bookedVsBilled: "Where most partner gap lives.",
      renewals: "Each PO is its own renewal line.",
    },
    callouts: [
      {
        tone: "note",
        body: "company_id is mandatory on POs and Replacement POs — for billing attribution and to keep company records inside Cinch clean. These links are the biggest gap today.",
      },
    ],
  },

  license: {
    key: "license",
    label: "License Agreement",
    badge: { text: "Allocation", tone: "yellow" },
    summary: "Allocation/capacity-based billing — one agreement covering as many companies as fit the licensed allotment.",
    lifecycle: [
      { stage: "Create", title: "Sign license", detail: "licensed contact allotment" },
      { stage: "Start", title: "License fee bills", detail: "first full month" },
      { stage: "Live", title: "Evergreen + add companies", detail: "within cap = no new billing" },
      { stage: "Grow/End", title: "Raise cap / Churn" },
    ],
    whenToUse:
      "Billing is allocation/capacity-based, not per-company. The licensee signs up as many companies as they want under one agreement; as long as total usage stays within their licensed contact allotment, no additional billing — it all rolls up under a single number. Contrast a reseller, who is billed per company via POs.",
    billingStart:
      "Otherwise New-like. Required at creation: ACCOUNT_ID, license/platform fee line + MRR, TERM_MONTHS, IS_AUTO_RENEW, NOTICE_PERIOD_DAYS, billing effective date. Billing start = first full month the license fee bills.",
    termRenewal:
      "Term + evergreen renewal as New (unless a fixed multi-year license). Replacement License = legal re-paper. Churn/debook as New.",
    keyPoints: {
      heading: "Expansion & coexistence",
      items: [
        "Expansion/upsell = raising the licensed cap (an overage event), not adding per-company lines.",
        "An account can hold both a license (pooled base batch) and a reseller agreement (base + higher per-company allocation).",
      ],
    },
    callouts: [
      {
        tone: "note",
        heading: "Model additions",
        body: "A licensed-allocation/cap field on the license, base + per-company allocation on the reseller, and rolled-up usage measured against them to detect overage.",
      },
    ],
  },
};

/**
 * Resolve a Cinch contract-type display name to a playbook archetype key.
 * Returns null when the doc has no archetype for it (e.g. Referral) so the view
 * can show a graceful "no playbook yet" state. Order matters — more specific
 * keywords are tested first (e.g. "Replacement Purchase Order" → replacement).
 */
export function keyForContractType(name: string | null | undefined): string | null {
  const n = (name ?? "").toLowerCase();
  if (!n) return null;
  if (n.includes("referral")) return null; // not covered by the playbook
  if (n.includes("replacement")) return "replacement";
  if (n.includes("reseller")) return "reseller";
  if (n.includes("license")) return "license";
  if (n.includes("upsell")) {
    return n.includes("purchase order") || /\bpo\b/.test(n) ? "poupsell" : "upsell";
  }
  if (n.includes("purchase order") || /\bpo\b/.test(n)) return "po";
  if (n.includes("new")) return "new";
  return null;
}

export interface ResolvedPlaybook {
  playbook: ContractTypePlaybook;
  /** True when this is the static archetype default (no real/edited data yet). */
  emulated: boolean;
  /** The archetype key matched, or null when there's no match. */
  matchedKey: string | null;
}

/**
 * Merge the static archetype default with an optional override (a future Ops-
 * persisted / backend payload of the same shape). The override wins field-by-
 * field; anything it omits falls back to the archetype. `emulated` is true while
 * there's no override, which drives the "Waiting real data…" notice.
 */
export function resolvePlaybook(
  name: string | null | undefined,
  override?: Partial<ContractTypePlaybook> | null,
): ResolvedPlaybook | null {
  const matchedKey = keyForContractType(name);
  const base = matchedKey ? PLAYBOOK[matchedKey] : undefined;
  if (!base && !override) return null;
  const playbook = { ...(base ?? { key: "custom", label: name ?? "Contract type", summary: "" }), ...(override ?? {}) };
  return { playbook, emulated: !override, matchedKey };
}
